import getUuid from 'widget/helpers/uuid';
import { getMappingFieldVariables } from './mappingFieldsUtils';

export function getFieldVariables(valueMap, registryMap = []) {
  const variables = getMappingFieldVariables(valueMap, registryMap);
  return variables.filter(
    variable => variable.name != null && variable.name !== ''
  );
}

export function findStepAndUpdate(steps, stepId, data) {
  if (stepId == null) return data;

  return steps.map(step => {
    const stepUpdates = {};

    if (step.default) {
      if (step.default.id === stepId) {
        stepUpdates.default = {
          ...step.default,
          ...data,
        };
      } else {
        stepUpdates.default = {
          ...step.default,
          steps: findStepAndUpdate(step.default.steps, stepId, data),
        };
      }
    }

    if (step.branches) {
      stepUpdates.branches = step.branches.map(branch => {
        if (branch.id === stepId) {
          return {
            ...branch,
            ...data,
          };
        }
        return {
          ...branch,
          steps: findStepAndUpdate(branch.steps, stepId, data),
        };
      });
    }

    if (step.action === 'for_each' && step.steps) {
      stepUpdates.steps = findStepAndUpdate(step.steps, stepId, data);
    }

    if (step.id === stepId) {
      Object.assign(stepUpdates, data);
    }

    return {
      ...step,
      ...stepUpdates,
    };
  });
}

function cloneWithNewIds(step, idMap) {
  const copy = { ...step, id: getUuid() };
  idMap.set(step.id, copy.id);

  if (step.default) {
    copy.default = {
      ...step.default,
      id: getUuid(),
      steps: (step.default.steps || []).map(s => cloneWithNewIds(s, idMap)),
    };
    idMap.set(step.default.id, copy.default.id);
  }

  if (step.branches) {
    copy.branches = step.branches.map(branch => {
      const branchCopy = {
        ...branch,
        id: getUuid(),
        steps: (branch.steps || []).map(s => cloneWithNewIds(s, idMap)),
      };
      idMap.set(branch.id, branchCopy.id);
      return branchCopy;
    });
  }

  if (step.action === 'for_each' && step.steps) {
    copy.steps = step.steps.map(s => cloneWithNewIds(s, idMap));
  }

  return copy;
}

function remapJumpTargets(step, idMap) {
  const copy = { ...step };

  if (step.action === 'jump_to_step') {
    const targetId = step.attributes?.step_id;
    if (idMap.has(targetId)) {
      copy.attributes = { ...step.attributes, step_id: idMap.get(targetId) };
    }
  }

  if (step.default) {
    copy.default = {
      ...step.default,
      steps: step.default.steps.map(s => remapJumpTargets(s, idMap)),
    };
  }

  if (step.branches) {
    copy.branches = step.branches.map(branch => ({
      ...branch,
      steps: branch.steps.map(s => remapJumpTargets(s, idMap)),
    }));
  }

  if (step.action === 'for_each' && step.steps) {
    copy.steps = step.steps.map(s => remapJumpTargets(s, idMap));
  }

  return copy;
}

/**
 * Returns a copy of the step tree with fresh ids on the step itself and on
 * every nested container (branches, default branch, for_each body), so a
 * pasted copy never collides with the original ids. jump_to_step targets
 * that point inside the copied subtree are remapped to the new ids.
 */
export function regenerateStepIds(step) {
  const idMap = new Map();
  return remapJumpTargets(cloneWithNewIds(step, idMap), idMap);
}

/**
 * Appends `extra` to the end of a flow. A branch is terminal, so when the flow
 * ends with one the steps continue inside its (deepest) default arm.
 */
function appendToFlow(steps, extra) {
  if (extra.length === 0) return steps;
  const last = steps[steps.length - 1];
  if (last?.action !== 'branch') return [...steps, ...extra];

  return [
    ...steps.slice(0, -1),
    {
      ...last,
      default: {
        ...last.default,
        steps: appendToFlow(last.default.steps, extra),
      },
    },
  ];
}

/**
 * Inserts a step at `index` and returns the new steps array. A branch is a
 * terminal step: the steps after the insertion point continue in its default
 * arm, after any default-arm steps the branch already carries (a pasted or
 * moved branch brings its whole subtree along).
 */
export function insertStepAt(steps, index, newStep) {
  if (newStep.action !== 'branch') {
    const result = Array.from(steps);
    result.splice(index, 0, newStep);
    return result;
  }

  const branchStep = {
    ...newStep,
    default: {
      ...newStep.default,
      steps: appendToFlow(newStep.default.steps, steps.slice(index)),
    },
  };
  return steps.slice(0, index).concat(branchStep);
}

export function findStepAndDelete(steps, stepId) {
  return steps
    .filter(step => step.id !== stepId)
    .map(step => {
      const updates = {};
      if (step.default) {
        updates.default = {
          ...step.default,
          steps: findStepAndDelete(step.default.steps, stepId),
        };
      }
      if (step.branches) {
        updates.branches = step.branches.map(branch => ({
          ...branch,
          steps: findStepAndDelete(branch.steps, stepId),
        }));
      }
      if (step.action === 'for_each' && step.steps) {
        updates.steps = findStepAndDelete(step.steps, stepId);
      }
      return { ...step, ...updates };
    });
}

export function findStep(steps, stepId) {
  for (let i = 0; i < steps.length; i += 1) {
    const step = steps[i];
    if (step.id === stepId) return step;

    if (step.default) {
      if (step.default.id === stepId) return step.default;
      const found = findStep(step.default.steps, stepId);
      if (found) return found;
    }

    if (step.branches) {
      for (let j = 0; j < step.branches.length; j += 1) {
        const branch = step.branches[j];
        if (branch.id === stepId) return branch;
        const found = findStep(branch.steps, stepId);
        if (found) return found;
      }
    }

    if (step.action === 'for_each' && step.steps) {
      const found = findStep(step.steps, stepId);
      if (found) return found;
    }
  }
  return null;
}

export function getStepPath(steps, stepId, parentPath = []) {
  const path = Array.from(parentPath);
  for (let i = 0; i < steps.length; i += 1) {
    const step = steps[i];
    path.push(step.id);
    if (step.id === stepId) return path;

    if (step.default) {
      const found = getStepPath(step.default.steps, stepId, path);
      if (found) return found;
    }

    if (step.branches) {
      for (let j = 0; j < step.branches.length; j += 1) {
        const branch = step.branches[j];
        const found = getStepPath(branch.steps, stepId, path);
        if (found) return found;
      }
    }

    if (step.action === 'for_each' && step.steps) {
      const found = getStepPath(step.steps, stepId, path);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Internal recursive helper for computeStepVariablesMap.
 * Populates the map and returns the accumulated output_mapping variables
 * at this level (including default branch accumulations), eliminating
 * the need for a separate tree traversal to collect output mappings.
 */
function buildVariablesMap(steps, actionMap, inheritedVariables, map) {
  let accumulated = [];

  for (let i = 0; i < steps.length; i += 1) {
    const step = steps[i];
    const action = actionMap.get(step.action) ?? {};

    const visibleVars = [...accumulated];
    if (action.available_data) {
      const availableDataFields = action.available_data;
      visibleVars.push(
        ...Object.keys(availableDataFields).map(name => ({
          ...availableDataFields[name],
          name,
          source: step.id,
          prefix: '#',
        }))
      );
    }
    map.set(step.id, [...inheritedVariables, ...visibleVars]);

    if (step.output_mapping) {
      const outputVars = getFieldVariables(
        step.output_mapping,
        action.output_mapping_fields
      );
      accumulated.push(
        ...outputVars.map(variable => ({
          ...variable,
          source: step.id,
        }))
      );
    }

    if (step.branches) {
      const branchInherited = [...inheritedVariables, ...accumulated];
      for (let j = 0; j < step.branches.length; j += 1) {
        const branch = step.branches[j];
        if (branch.id) map.set(branch.id, branchInherited);
        if (branch.steps) {
          buildVariablesMap(branch.steps, actionMap, branchInherited, map);
        }
      }
    }

    if (step.default) {
      const defaultInherited = [...inheritedVariables, ...accumulated];
      if (step.default.id) map.set(step.default.id, defaultInherited);
      if (step.default.steps) {
        const defaultAccumulated = buildVariablesMap(
          step.default.steps,
          actionMap,
          defaultInherited,
          map
        );
        accumulated.push(...defaultAccumulated);
      }
    }

    if (step.action === 'for_each' && step.steps) {
      const forEachInherited = [...inheritedVariables, ...accumulated];
      const fieldMapping = step.attributes?.field_mapping || [];
      const fieldMappingVars = fieldMapping
        .filter(m => m.variable_name)
        .map(m => ({
          name: m.variable_name,
          source: step.id,
          type: m.type || 'text',
        }));
      const loopVariables = [...forEachInherited, ...fieldMappingVars];
      // Inner output mappings do NOT propagate upward (scoped to loop)
      buildVariablesMap(step.steps, actionMap, loopVariables, map);
    }
  }

  return accumulated;
}

/**
 * Builds a Map<stepId, variables[]> for the entire step tree in a single
 * O(N) pass. Each entry contains the variables visible to that step
 * (inheritedVariables + accumulated output mappings + available_data).
 */
export function computeStepVariablesMap(steps, actions, inheritedVariables) {
  const map = new Map();
  const actionMap = new Map(actions.map(a => [a.action, a]));
  buildVariablesMap(steps, actionMap, inheritedVariables, map);
  return map;
}
